import { useLocation, Link } from 'react-router-dom';
import { HelpCircle, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { moduleForPath, groupForPath, MODULE_BY_PATH } from '@/lib/modules';

/**
 * What this screen is, available from the screen itself.
 *
 * The problem this solves is real and was measured: forty-odd screens carrying
 * labels like "Putaway", "Pipe Tally", "Charge Types" and "PO Visibility", each
 * naming a noun and none explaining the job. A new planner could not tell what
 * half the product was for, and nothing in the interface would tell them.
 *
 * Three decisions worth recording, because each rejects a more obvious option:
 *
 * It is a popover, not a banner. A permanent explanation on every page is read
 * once and becomes furniture on the second visit — and furniture that occupies
 * the most valuable strip of the screen. This sits behind one quiet control next
 * to the title, costs nothing when ignored, and is in the same place every time.
 *
 * It shows the flow, not just the definition. Knowing what Putaway is matters
 * less than knowing that receipts arrive into it and the yard is what comes out.
 * Upstream and downstream are real links, so the explanation is also a way to
 * travel — which is how somebody learns the shape of a system.
 *
 * It reads the registry rather than taking props. PageHeader renders it for
 * every page automatically, so a new screen is explained the moment it is added
 * to `lib/modules.ts`, and no screen can carry an explanation that disagrees
 * with the one in the rail.
 */
export function ModuleIntro() {
  const { pathname } = useLocation();
  const entry = moduleForPath(pathname);
  const group = groupForPath(pathname);

  // No entry means an unregistered route — login, the design system. Rendering
  // an empty popover trigger there would promise an answer we do not have.
  if (!entry) return null;

  const related = (paths: string[] | undefined) =>
    (paths ?? []).map((p) => MODULE_BY_PATH[p]).filter(Boolean);

  const feeds = related(entry.upstream);
  const serves = related(entry.downstream);

  return (
    <Popover>
      <PopoverTrigger
        className="rounded-full p-0.5 text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`What is ${entry.title}?`}
      >
        <HelpCircle className="h-[18px] w-[18px]" />
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[26rem] max-w-[calc(100vw-2rem)] p-0">
        <div className="border-b px-4 py-3">
          {group && (
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.title}
            </p>
          )}
          <h3 className="text-sm font-semibold text-foreground">{entry.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{entry.purpose}</p>
        </div>

        <div className="border-b px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Answers
          </p>
          {/* The question is set in the reader's own voice, so it reads as the
              thing they came here wondering rather than as a feature claim. */}
          <p className="mt-1 text-sm italic text-foreground">“{entry.question}”</p>
        </div>

        {(feeds.length > 0 || serves.length > 0) && (
          <div className="space-y-2.5 border-b px-4 py-3">
            {feeds.length > 0 && (
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <ArrowDownLeft className="h-3 w-3" />
                  Fed by
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {feeds.map((m) => (
                    <Link
                      key={m.path}
                      to={m.path}
                      className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                    >
                      {m.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {serves.length > 0 && (
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <ArrowUpRight className="h-3 w-3" />
                  Feeds
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {serves.map((m) => (
                    <Link
                      key={m.path}
                      to={m.path}
                      className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                    >
                      {m.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {entry.terms && entry.terms.length > 0 && (
          <div className="px-4 py-3">
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Terms on this screen
            </p>
            <dl className="space-y-1.5">
              {entry.terms.map((t) => (
                <div key={t.term}>
                  <dt className="text-xs font-semibold text-foreground">{t.term}</dt>
                  <dd className="text-xs leading-relaxed text-muted-foreground">{t.means}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className="border-t bg-muted/40 px-4 py-2">
          <Link
            to="/guide"
            className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            See how this fits the whole operation →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
